import { OrganizationSite } from "@/generated/graphql";
import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useProfile } from "@/components/Auth/ProfileProvider";

type MultiSiteContextType = {
  multisiteEnabled: boolean; // Is the org allowed to use multisite?
  usingMultisite: boolean; // Are we currently using multisite?
  currentSite: OrganizationSite | null;
  sitesAvailable: OrganizationSite[];
  setCurrentSite: (site: OrganizationSite | null) => void;
  persistCurrentSite: (value: OrganizationSite | null) => void;
};

const MultiSiteContext = createContext<MultiSiteContextType>({
  multisiteEnabled: false,
  usingMultisite: false,
  currentSite: null,
  setCurrentSite: () => {},
  persistCurrentSite: () => {},
  sitesAvailable: [], 
});

export const useMultiSite = () => useContext(MultiSiteContext);

const LOCAL_STORAGE_KEY = "palomar_multiSiteSelection";

export const MultiSiteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile } = useProfile();
  const sitesAvailable = (profile?.organization?.sites || []).filter(
    (site): site is OrganizationSite => site !== null
  );
  // Initialize state with all sites by default
  const [currentSite, setCurrentSite] = useState<OrganizationSite | null>(null);
  const hasHydrated = useRef(false);

  // Effect for hydrating state from localStorage on mount or when sitesAvailable changes
  useEffect(() => {
    // Prevent re-hydration after initial load
    if (hasHydrated.current) {
      return;
    }

    // Don't try to hydrate if no sites are available yet
    if (sitesAvailable.length === 0) {
      return;
    }

    try {
      const storedValue = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (storedValue) {
        const storedSiteId = JSON.parse(storedValue) as string;
        const sitesAvailableById = new Map(sitesAvailable.map((s) => [s.id, s]));
        const validStoredSite = sitesAvailableById.get(storedSiteId);
        
        if (validStoredSite) {
          setCurrentSite(validStoredSite);
        } else {
          setCurrentSite(null);
        } 
      }
      } catch (error) {
      console.error("Failed to load or parse multi-site selection from localStorage:", error);
    }

    hasHydrated.current = true;
  }, [sitesAvailable]);

  // Effect for persisting state changes to localStorage
  const persistCurrentSite = useCallback((value: OrganizationSite | null) => {
    try {
      const siteIdToStore = value?.id;
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(siteIdToStore));
      setCurrentSite(value);
    } catch (error) {
      console.error("Failed to save multi-site selection to localStorage:", error);
    }
  }, []);

  const multisiteEnabled = profile?.organization?.allowMultisite || false;
  const usingMultisite = multisiteEnabled && sitesAvailable.length > 1;
  return (
    <MultiSiteContext.Provider value={{ currentSite, setCurrentSite, sitesAvailable, multisiteEnabled, persistCurrentSite, usingMultisite}}>
      {children}
    </MultiSiteContext.Provider>
  );
};
